import ActiveParticipantsWidget from 'tio-employee/components/partner-dashboard/widgets/active-participants';
import ApproverWorkloadWidget from 'tio-employee/components/partner-dashboard/widgets/approver-workload';
import ApproverGuideWidget from 'tio-employee/components/partner-dashboard/widgets/approver-guide';
import DecisionPaceWidget from 'tio-employee/components/partner-dashboard/widgets/decision-pace';
import EligibilityLatestRunWidget from 'tio-employee/components/partner-dashboard/widgets/eligibility-latest-run';
import EmployeesHeadcountWidget from 'tio-employee/components/partner-dashboard/widgets/employees-headcount';
import InReviewWidget from 'tio-employee/components/partner-dashboard/widgets/in-review';
import PaidToDateWidget from 'tio-employee/components/partner-dashboard/widgets/paid-to-date';
import RecentDecisionsWidget from 'tio-employee/components/partner-dashboard/widgets/recent-decisions';
import SlrProgramWidget from 'tio-employee/components/partner-dashboard/widgets/slr-program';
import SubmittedPulseWidget from 'tio-employee/components/partner-dashboard/widgets/submitted-pulse';
import TaProgramsWidget from 'tio-employee/components/partner-dashboard/widgets/ta-programs';
import TaTrendsWidget from 'tio-employee/components/partner-dashboard/widgets/ta-trends';
import WaitingOnOthersWidget from 'tio-employee/components/partner-dashboard/widgets/waiting-on-others';
import WaitingOnYouWidget from 'tio-employee/components/partner-dashboard/widgets/waiting-on-you';
import {
  defineWidget,
  type AttentionSource,
  type RegisteredWidget,
} from 'tio-employee/utils/partner-dashboard/registry';
import {
  APPROVAL_QUEUE_SOURCE,
  loadQueueSnapshot,
  type QueueSnapshot,
} from 'tio-employee/utils/partner-dashboard/sources/approval-queue';
import {
  loadDecisionStats,
  loadRecentDecisions,
  type DecisionStats,
  type RecentDecision,
} from 'tio-employee/utils/partner-dashboard/sources/recent-decisions';
import {
  STRANDED_APPROVALS_SOURCE,
  loadReviewerLoad,
  type ReviewerLoad,
} from 'tio-employee/utils/partner-dashboard/sources/approval-health';
import {
  ELIGIBILITY_FILE_SOURCE,
  loadLatestEligibilityRun,
  type EligibilityRun,
} from 'tio-employee/utils/partner-dashboard/sources/eligibility';
import {
  loadHeadcount,
  type Headcount,
} from 'tio-employee/utils/partner-dashboard/sources/headcount';
import {
  loadSlrProgram,
  type SlrProgramSummary,
} from 'tio-employee/utils/partner-dashboard/sources/slr';
import {
  SLA_BREACHES_SOURCE,
  hasTuitionAssistance,
  loadActiveParticipants,
  loadProgramPulse,
  loadPrograms,
  loadTrends,
  type ProgramPulse,
  type ProgramSummary,
  type TrendWeek,
} from 'tio-employee/utils/partner-dashboard/sources/ta-analytics';

/**
 * Every partner dashboard widget, in the order it renders within its section.
 * Add a widget here with `defineWidget`; the route loads and the page renders
 * only the ones the viewer's personas drive.
 */
export const PARTNER_DASHBOARD_WIDGETS: readonly RegisteredWidget[] = [
  defineWidget<QueueSnapshot>({
    id: 'waiting-on-you',
    section: 'hero',
    drivenBy: ['approver', 'supervisor'],
    load: loadQueueSnapshot,
    component: WaitingOnYouWidget,
  }),
  defineWidget<QueueSnapshot>({
    id: 'waiting-on-others',
    section: 'hero',
    drivenBy: ['approver', 'supervisor'],
    load: loadQueueSnapshot,
    component: WaitingOnOthersWidget,
  }),
  defineWidget<DecisionStats>({
    id: 'decision-pace',
    section: 'hero',
    drivenBy: ['approver', 'supervisor'],
    load: loadDecisionStats,
    component: DecisionPaceWidget,
  }),
  defineWidget<ProgramPulse>({
    id: 'submitted-pulse',
    section: 'hero',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadProgramPulse,
    component: SubmittedPulseWidget,
  }),
  defineWidget<ProgramPulse>({
    id: 'in-review',
    section: 'hero',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadProgramPulse,
    component: InReviewWidget,
  }),
  defineWidget<ProgramPulse>({
    id: 'paid-to-date',
    section: 'hero',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadProgramPulse,
    component: PaidToDateWidget,
  }),
  defineWidget<number>({
    id: 'active-participants',
    section: 'hero',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadActiveParticipants,
    component: ActiveParticipantsWidget,
  }),
  defineWidget<TrendWeek[]>({
    id: 'ta-trends',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadTrends,
    component: TaTrendsWidget,
  }),
  defineWidget<ProgramSummary[]>({
    id: 'ta-programs',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadPrograms,
    component: TaProgramsWidget,
  }),
  defineWidget<ReviewerLoad[]>({
    id: 'approver-workload',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    requires: hasTuitionAssistance,
    load: loadReviewerLoad,
    component: ApproverWorkloadWidget,
  }),
  defineWidget<SlrProgramSummary | null>({
    id: 'slr-program',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    load: loadSlrProgram,
    component: SlrProgramWidget,
  }),
  defineWidget<EligibilityRun | null>({
    id: 'eligibility-latest-run',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    load: loadLatestEligibilityRun,
    component: EligibilityLatestRunWidget,
  }),
  defineWidget<Headcount>({
    id: 'employees-headcount',
    section: 'pertinent',
    drivenBy: ['accountOwner', 'partnerAdmin'],
    load: loadHeadcount,
    component: EmployeesHeadcountWidget,
  }),
  defineWidget<RecentDecision[]>({
    id: 'recent-decisions',
    section: 'pertinent',
    drivenBy: ['approver', 'supervisor'],
    load: loadRecentDecisions,
    component: RecentDecisionsWidget,
  }),
  defineWidget<null>({
    id: 'approver-guide',
    section: 'explore',
    drivenBy: ['approver', 'supervisor'],
    load: () => Promise.resolve(null),
    component: ApproverGuideWidget,
  }),
];

/**
 * Every Needs Your Attention source. Their items merge into one list sorted by
 * deadline; the route loads only the ones the viewer's personas drive.
 */
export const PARTNER_DASHBOARD_ATTENTION_SOURCES: readonly AttentionSource[] = [
  SLA_BREACHES_SOURCE,
  STRANDED_APPROVALS_SOURCE,
  ELIGIBILITY_FILE_SOURCE,
  APPROVAL_QUEUE_SOURCE,
];
